import { clientCreateSchema, phoneSearchDigits, type ClientDto, type ClientListItem } from '@kominiarz/shared';
import { useState } from 'react';
import { ApiError, errorMessage } from '../lib/api';
import { useCreateClient } from '../lib/clientsApi';
import { AddressField, type Coords } from './AddressField';
import { Alert } from './Alert';
import { OfflineNotice } from './OfflineNotice';
import { useOnline } from '../lib/appStatus';
import { Button } from './Button';
import { Dialog } from './Dialog';
import { SimilarClients } from './SimilarClients';
import { TextArea } from './form/TextArea';
import { TextInput } from './form/TextInput';

interface NewClientDialogProps {
  open: boolean;
  /** What the user typed in the search box: a name or a phone number. */
  initialQuery: string;
  /** Address already typed in the visit form; becomes the client's first address. */
  initialAddress: string;
  onClose: () => void;
  onCreated: (client: ClientDto) => void;
  /** The client turned out to exist already: use that one. */
  onPickExisting: (client: ClientListItem) => void;
}

/** Adds a client without leaving the visit form. */
export function NewClientDialog({ open, initialQuery, initialAddress, onClose, onCreated, onPickExisting }: NewClientDialogProps) {
  const looksLikePhone = phoneSearchDigits(initialQuery).length >= 6;
  const [name, setName] = useState(looksLikePhone ? '' : initialQuery);
  const [phone, setPhone] = useState(looksLikePhone ? initialQuery : '');
  const [address, setAddress] = useState(initialAddress);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useCreateClient();
  const online = useOnline();

  async function save() {
    const body = {
      name,
      phones: phone.trim() ? [{ phone }] : [],
      objects: address.trim() ? [{ address, note: note.trim() || null, lat: coords?.lat ?? null, lng: coords?.lng ?? null }] : [],
    };
    const parsed = clientCreateSchema.safeParse(body);
    if (!parsed.success) {
      const fields: Record<string, string> = {};
      for (const i of parsed.error.issues) {
        const key = i.path[0] === 'phones' ? 'phone' : i.path[0] === 'objects' ? 'address' : String(i.path[0]);
        fields[key] ??= i.message;
      }
      setErrors(fields);
      return;
    }
    try {
      const res = await create.mutateAsync(parsed.data);
      onCreated(res.client);
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) {
        setErrors({ name: err.fields.name ?? '', phone: err.fields['phones.0.phone'] ?? '', address: err.fields['objects.0.address'] ?? '' });
      }
    }
  }

  const showAlert = create.error && !(create.error instanceof ApiError && Object.keys(create.error.fields).length);

  return (
    <Dialog
      open={open}
      title="Nowy klient"
      onClose={onClose}
      actions={
        <>
          <Button variant="primary" size="lg" onClick={save} disabled={create.isPending || !online}>
            {create.isPending ? 'Zapisywanie…' : 'Dodaj klienta'}
          </Button>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Anuluj
          </Button>
        </>
      }
    >
      <OfflineNotice />
      <TextInput
        label="Imię i nazwisko albo nazwa firmy"
        hint="Można pominąć, jeśli jest telefon – klient będzie widoczny pod numerem."
        autoComplete="off"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={errors.name || undefined}
      />
      <TextInput
        type="tel"
        label="Telefon"
        inputMode="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        error={errors.phone || undefined}
      />
      <SimilarClients
        name={name}
        phone={phone}
        action={(c) => (
          <Button size="sm" variant="secondary" onClick={() => onPickExisting(c)}>
            Wybierz
          </Button>
        )}
      />
      <AddressField
        label="Adres"
        optional
        hint="Zapisze się jako adres klienta i od razu trafi do wizyty."
        value={address}
        coords={coords}
        onType={(a) => {
          setAddress(a);
          setCoords(null);
        }}
        onPick={(a, c) => {
          setAddress(a);
          setCoords(c);
        }}
        onPin={setCoords}
        error={errors.address || undefined}
      />
      {address.trim() && (
        <TextArea
          label="Uwagi do adresu"
          optional
          rows={2}
          placeholder="np. kod do bramy, pies, piec w piwnicy"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      )}
      {showAlert && <Alert kind="error">{errorMessage(create.error)}</Alert>}
    </Dialog>
  );
}
